import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import { auth, db } from '../firebaseConfig';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  orderBy
} from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import { FaSpinner } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import '../Style/EditTestReport.css';

/* ------------------ Report Types ------------------ */
const REPORT_TYPES = {
  AC_TEST: 'AC_TEST',
  FINAL_INSPECTION: 'FINAL_INSPECTION',
  CYLINDER: 'CYLINDER',
  DRIFTER: 'DRIFTER'
};
const REPORT_TYPE_LABELS = {
  [REPORT_TYPES.AC_TEST]: 'AC Test Report',
  [REPORT_TYPES.FINAL_INSPECTION]: 'Final Inspection Report',
  [REPORT_TYPES.CYLINDER]: 'Cylinder Test Report',
  [REPORT_TYPES.DRIFTER]: 'Drifter Test Report'
};

/* ---------- Defaults / legacy helpers ---------- */
const DEFAULT_FI_ITEMS = [
  'Bearings (Main)','Suction Liner','Engine Liner','Frame Insert','Bearings (Secondary)',
  'Impeller','Springs','Engine Cover','Suction Cover','Lantern Ring','Restrictor Ring',
  'Adjusting Plug','Shaft & Sleeve','Water Flinger','Hex Bolts','Side Glass & Breather'
];

const cylinderLowList = [
  ['airRemoved','Air Removed'],
  ['lowVibration','No Vibration / Locking'],
  ['lowStaticLeak','No Static Seal Leak'],
  ['lowRodSealLeak','No Rod Seal Leak'],
  ['lowWeldLeak','No Weld Leak']
];
const cylinderHighList = [
  ['highVibration','No Vibration / Locking'],
  ['highRodSealLeak','No Rod Seal Leak'],
  ['highStaticSealLeak','No Static Seal Leak'],
  ['highWeldLeak','No Weld Leak'],
  ['fullExtHold','Hold @ Full Extension'],
  ['fullRetHold','Hold @ Full Retraction']
];
const drifterShiftNotesList = [
  'Check air & lubrication oil at shank adapter/front guide',
  'Inspect shank adapter threads',
  'Every 40 hours grease gear & tighten unions'
];

/* ============== Small UI primitives ============== */
const Field = React.memo(function Field({
  id, label, value, onChange, type='text', placeholder, className
}) {
  return (
    <div className={`field ${className||''}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        autoComplete="off"
      />
    </div>
  );
});

const CheckItem = React.memo(function CheckItem({
  id, label, checked, onChange, dataGroup
}) {
  return (
    <label className="chk">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        data-group={dataGroup}
      />
      <span className="box" />
      <span className="lbl">{label}</span>
    </label>
  );
});

const CheckGrid = React.memo(({ children }) => (
  <div className="check-grid-adv">{children}</div>
));

/* Utility */
const genId = () => Math.random().toString(36).slice(2) + Date.now();

/* ============== Main Component ============== */
export default function EditTestReport() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [jobs, setJobs]       = useState([]);
  const [customers, setCustomers] = useState([]);

  const [reportType, setReportType] = useState(REPORT_TYPES.AC_TEST);
  const [reportId, setReportId] = useState('');

  const [header, setHeader] = useState({
    customer:'',
    jobId:'',
    status:'Pending',
    date:''
  });

  /* AC Test State */
  const [acTest, setAcTest] = useState({
    make:'', kw_hp:'', frameSize:'', poles:'', speed:'', unitNo:'',
    ratedVoltage:'', connection:'', rotorBalanced:false,
    ir1000:'', growler:'', condBurnt:false, condWet:false,
    condMech:false, condSingle:false, condOther:'',
    ambientTemp:'', coreTemp:'', coreWatt:'',
    loadVolt:'', phaseA:'', phaseB:'', phaseC:'',
    pressureVal:'', pressureDur:'', irPost:'',
    runVolt:'', lockedA:'', lockedB:'', lockedC:'', opVolt:'',
    bearingDE:'', bearingNDE:'', efficiency:'',
    vibDE:'', vibNDE:'', testedBy:''
  });

  /* Final Inspection (dynamic checklist) */
  const [finalInspection, setFinalInspection] = useState({
    description: 'C FRAME PUMP',
    items: [],               // [{id,label,passed}]
    testedBy: ''
  });
  const [fiNewLabel, setFiNewLabel] = useState('');

  /* Cylinder */
  const [cylinder, setCylinder] = useState({
    serialNo:'', description:'OUTRIGGER CYLINDER', rodDia:'', barrelDia:'',
    openCentres:'', closedCentres:'',
    airRemoved:false, lowVibration:false, lowStaticLeak:false,
    lowRodSealLeak:false, lowWeldLeak:false,
    highVibration:false, highRodSealLeak:false, highStaticSealLeak:false,
    highWeldLeak:false, fullExtHold:false, fullRetHold:false,
    oilCondition:'', oilTemp:'', oilCleanliness:'', maxPressure:'',
    finalResult:'', testedBy:''
  });

  /* Drifter */
  const [drifter, setDrifter] = useState({
    drifterType:'RD314', serialNo:'', oilTempBefore:'', oilTempAfter:'',
    testingPressure:'', percussionTested:false, percussionTime:'',
    percussionRemarks:'', rotationTested:false, rotationTime:'',
    rotationRemarks:'', notesShift:true, checkedBy:''
  });

  /* ------------------ Helpers ------------------ */
  const detectLegacyType = (data) => {
    if (data.finalInspection) return REPORT_TYPES.FINAL_INSPECTION;
    if (data.cylinder)        return REPORT_TYPES.CYLINDER;
    if (data.drifter)         return REPORT_TYPES.DRIFTER;
    return REPORT_TYPES.AC_TEST;
  };

  const extractFlatAC = data => ({
    make:data.make||'', kw_hp:data.kw_hp||'', frameSize:data.frameSize||'',
    poles:data.poles||'', speed:data.speed||'', unitNo:data.unitNo||'',
    ratedVoltage:data.ratedVoltage||'', connection:data.connection||'',
    rotorBalanced:data.rotorBalanced||false,
    ir1000:data.ir1000||'', growler:data.growler||'',
    condBurnt:data.condBurnt||false, condWet:data.condWet||false,
    condMech:data.condMech||false, condSingle:data.condSingle||false,
    condOther:data.condOther||'', ambientTemp:data.ambientTemp||'',
    coreTemp:data.coreTemp||'', coreWatt:data.coreWatt||'',
    loadVolt:data.loadVolt||'', phaseA:data.phaseA||'',
    phaseB:data.phaseB||'', phaseC:data.phaseC||'',
    pressureVal:data.pressureVal||'', pressureDur:data.pressureDur||'',
    irPost:data.irPost||'', runVolt:data.runVolt||'',
    lockedA:data.lockedA||'', lockedB:data.lockedB||'',
    lockedC:data.lockedC||'', opVolt:data.opVolt||'',
    bearingDE:data.bearingDE||'', bearingNDE:data.bearingNDE||'',
    efficiency:data.efficiency||'', vibDE:data.vibDE||'',
    vibNDE:data.vibNDE||'', testedBy:data.testedBy||''
  });

  /* Hydrate state from Firestore doc */
  const hydrateFromDoc = (type, data) => {
    const hdr = data.header || {
      customer: data.customer || '',
      jobId: data.jobId || '',
      status: data.status || 'Pending',
      date: (data.header?.date) || (data.date || new Date().toISOString().slice(0,10))
    };
    setHeader({
      customer: hdr.customer || '',
      jobId: hdr.jobId || '',
      status: hdr.status || 'Pending',
      date: hdr.date || new Date().toISOString().slice(0,10)
    });

    if (type === REPORT_TYPES.AC_TEST) {
      if (data.acSections) {
        const find = (section, key) => {
          const sec = data.acSections.find(s=>s.section===section);
          if(!sec) return '';
          const row = sec.data.find(r => Array.isArray(r) ? r[0]===key : r.label===key);
          return row ? (Array.isArray(row)?row[1]:row.value) : '';
        };
        setAcTest(prev => ({
          ...prev,
          make:find('Identification','Make') || '',
          kw_hp:find('Identification','kW/HP') || '',
          frameSize:find('Identification','Frame Size') || '',
          poles:find('Identification','Poles') || '',
          speed:find('Identification','SpeedRPM') || '',
          unitNo:find('Identification','UnitNo') || '',
          ratedVoltage:find('Electrical','RatedVoltage') || '',
          connection:find('Electrical','Connection') || '',
          rotorBalanced:find('Electrical','RotorBalanced') === true || find('Electrical','RotorBalanced') === 'true',
          ir1000:find('PriorRewind','IR1000V') || '',
          growler:find('PriorRewind','Growler') || '',
          condBurnt:find('PriorRewind','CondBurnt')===true,
          condWet:find('PriorRewind','CondWet')===true,
          condMech:find('PriorRewind','CondMech')===true,
          condSingle:find('PriorRewind','CondSingle')===true,
          condOther:find('PriorRewind','CondOther')||'',
          ambientTemp:find('PriorRewind','AmbientTemp')||'',
          coreTemp:find('PriorRewind','CoreTemp')||'',
          coreWatt:find('PriorRewind','CoreWattLoss')||'',
          loadVolt:find('AfterRewind','TestVoltage')||'',
          phaseA:find('AfterRewind','PhaseA')||'',
          phaseB:find('AfterRewind','PhaseB')||'',
          phaseC:find('AfterRewind','PhaseC')||'',
          pressureVal:find('AfterAssembly','PressureKV')||'',
          pressureDur:find('AfterAssembly','PressureDurationS')||'',
          irPost:find('AfterAssembly','IRPost')||'',
          runVolt:find('AfterAssembly','RunVolt')||'',
          lockedA:find('AfterAssembly','LockedRotorA')||'',
          lockedB:find('AfterAssembly','LockedRotorB')||'',
          lockedC:find('AfterAssembly','LockedRotorC')||'',
          opVolt:find('AfterAssembly','OperatingVolt')||'',
          bearingDE:find('Performance','BearingDE')||'',
          bearingNDE:find('Performance','BearingNDE')||'',
          efficiency:find('Performance','EfficiencyPct')||'',
          vibDE:find('Performance','VibrationDE')||'',
          vibNDE:find('Performance','VibrationNDE')||'',
          testedBy:find('SignOff','TestedBy')|| data.testedBy || ''
        }));
      } else {
        setAcTest(prev => ({...prev, ...extractFlatAC(data)}));
      }
    } else if (type === REPORT_TYPES.FINAL_INSPECTION) {
      const fi = data.finalInspection || {};
      let items = [];

      // New format
      if (Array.isArray(fi.finalInspectionItems)) {
        items = fi.finalInspectionItems.map(it => ({
          id: genId(),
          label: it.label || '',
          passed: !!it.passed
        }));
      }
      // Old format possibilities
      if (!items.length && Array.isArray(fi.checklist)) {
        items = fi.checklist.map(it => ({
          id: genId(),
          label: it.label || '',
          passed: !!it.passed
        }));
      }
      // Fallback to defaults if nothing
      if (!items.length) {
        items = DEFAULT_FI_ITEMS.map(lbl => ({ id: genId(), label: lbl, passed: false }));
      }

      const testedBy =
        (fi.signOff?.find(r => r.field === 'CheckedBy')?.value) ||
        (Array.isArray(fi.signOff) && Array.isArray(fi.signOff[0]) && fi.signOff[0][0] === 'CheckedBy' && fi.signOff[0][1]) ||
        fi.testedBy ||
        '';

      setFinalInspection({
        description: fi.description || 'C FRAME PUMP',
        items,
        testedBy
      });
    } else if (type === REPORT_TYPES.CYLINDER) {
      const cy = data.cylinder || {};
      const allPairs = [...cylinderLowList, ...cylinderHighList];
      const boolFlags = {};
      (cy.cylinderChecksLow||[]).forEach(o=>{
        const p = allPairs.find(([k,l])=>l===o.label);
        if(p) boolFlags[p[0]] = !!o.ok;
      });
      (cy.cylinderChecksHigh||[]).forEach(o=>{
        const p = allPairs.find(([k,l])=>l===o.label);
        if(p) boolFlags[p[0]] = !!o.ok;
      });
      const idvals = {};
      (cy.identity||[]).forEach(([k,v])=>idvals[k]=v);
      const resvals = {};
      (cy.results||[]).forEach(([k,v])=>resvals[k]=v);
      setCylinder(prev=>({
        ...prev,
        serialNo:idvals.SerialNo || '',
        description:idvals.Description || 'OUTRIGGER CYLINDER',
        rodDia:idvals.RodDia || '',
        barrelDia:idvals.BarrelDia || '',
        openCentres:idvals.OpenCentres || '',
        closedCentres:idvals.ClosedCentres || '',
        ...boolFlags,
        oilCondition:resvals.OilCondition || '',
        oilTemp:resvals.OilTemp || '',
        oilCleanliness:resvals.OilCleanliness || '',
        maxPressure:resvals.MaxPressure || '',
        finalResult:resvals.FinalResult || '',
        testedBy:resvals.TestedBy || ''
      }));
    } else if (type === REPORT_TYPES.DRIFTER) {
      const dr = data.drifter || {};
      const idMap = {};
      (dr.idSection||[]).forEach(([k,v])=>idMap[k]=v);
      const tempMap = {};
      (dr.temperatures||[]).forEach(([k,v])=>tempMap[k]=v);
      const percussion = dr.drifterPercussion || dr.percussion || [];
      const rotation  = dr.drifterRotation  || dr.rotation  || [];
      const findRow = (arr,label)=>arr.find(o=>o.label===label);
      setDrifter(prev=>({
        ...prev,
        drifterType:idMap.DrifterType || 'RD314',
        serialNo:idMap.SerialNo || '',
        testingPressure:idMap.TestingPressure || '',
        oilTempBefore:tempMap.OilBefore || '',
        oilTempAfter:tempMap.OilAfter || '',
        percussionTested: !!(findRow(percussion,'Percussion Tested')?.ok),
        percussionTime:(findRow(percussion,'Percussion Time (min)')?.value)||'',
        percussionRemarks:(findRow(percussion,'Percussion Remarks')?.value)||'',
        rotationTested: !!(findRow(rotation,'Rotation Tested')?.ok),
        rotationTime:(findRow(rotation,'Rotation Time (min)')?.value)||'',
        rotationRemarks:(findRow(rotation,'Rotation Remarks')?.value)||'',
        notesShift: (dr.drifterShiftNotes && dr.drifterShiftNotes.length>0) || false,
        checkedBy:(Array.isArray(dr.signOff) && Array.isArray(dr.signOff[0]) && dr.signOff[0][0]==='CheckedBy'
          ? dr.signOff[0][1]
          : (dr.signOff?.find(r=>r.field==='CheckedBy')?.value) ) || ''
      }));
    }
  };

  /* -------------- Data Load -------------- */
  useEffect(() => {
    const run = async () => {
      try {
        const user = auth.currentUser;
        if (!user) { navigate('/'); return; }

        const jSnap = await getDocs(query(collection(db,'Jobs'), orderBy('createdAt','desc')));
        setJobs(jSnap.docs.map(d=>d.data().jobId));

        const cSnap = await getDocs(collection(db,'Customers'));
        setCustomers(cSnap.docs.map(d=>({id:d.id, ...d.data()})));

        const ref = doc(db,'TestReports', id);
        const snap = await getDoc(ref);
        if(!snap.exists()) { toast.error('Report not found'); navigate('/Jobs'); return; }
        const data = snap.data();

        setReportId(data.reportId || '');
        const detectedType = data.reportType || detectLegacyType(data);
        setReportType(detectedType);
        hydrateFromDoc(detectedType, data);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load report.');
      } finally {
        setLoading(false);
      }
    };
    const unsub = auth.onAuthStateChanged(u=>{ if (u) run(); else navigate('/'); });
    return () => unsub();
  }, [id, navigate]);

  /* -------------- Change Handlers -------------- */
  const changeHeader = useCallback((e) => {
    const { id, value } = e.target;
    setHeader(prev => ({ ...prev, [id]: value }));
  }, []);

  const changeAC = useCallback(e => {
    const { id, value, type, checked } = e.target;
    setAcTest(prev => ({ ...prev, [id]: type==='checkbox'? checked : value }));
  }, []);

  // Final Inspection
  const changeFIDescOrTester = useCallback(e => {
    const { id, value } = e.target;
    setFinalInspection(prev => ({ ...prev, [id]: value }));
  }, []);

  const addFiItem = useCallback(() => {
    const label = fiNewLabel.trim();
    if (!label) return;
    setFinalInspection(prev => ({
      ...prev,
      items: [...prev.items, { id: genId(), label, passed: false }]
    }));
    setFiNewLabel('');
  }, [fiNewLabel]);

  const toggleFiPassed = useCallback((itemId, checked) => {
    setFinalInspection(prev => ({
      ...prev,
      items: prev.items.map(it => it.id===itemId ? { ...it, passed: checked } : it)
    }));
  }, []);

  const editFiLabel = useCallback((itemId, newLabel) => {
    setFinalInspection(prev => ({
      ...prev,
      items: prev.items.map(it => it.id===itemId ? { ...it, label: newLabel } : it)
    }));
  }, []);

  const deleteFiItem = useCallback(itemId => {
    setFinalInspection(prev => ({
      ...prev,
      items: prev.items.filter(it => it.id !== itemId)
    }));
  }, []);

  // Cylinder & Drifter
  const changeCylinder = useCallback(e => {
    const { id, value, type, checked } = e.target;
    setCylinder(prev => ({ ...prev, [id]: type==='checkbox'? checked : value }));
  }, []);

  const changeDrifter = useCallback(e => {
    const { id, value, type, checked } = e.target;
    setDrifter(prev => ({ ...prev, [id]: type==='checkbox'? checked : value }));
  }, []);

  /* -------------- Validation -------------- */
  const validate = () => {
    if(!reportId) return 'Report ID missing.';
    if(!header.customer) return 'Customer required.';
    if(!header.jobId) return 'Job No required.';
    if(!header.date) return 'Date required.';
    switch(reportType){
      case REPORT_TYPES.AC_TEST:
        if(!acTest.testedBy) return 'Tester required.';
        break;
      case REPORT_TYPES.FINAL_INSPECTION:
        if(!finalInspection.testedBy) return 'Checked By required.';
        break;
      case REPORT_TYPES.CYLINDER:
        if(!cylinder.serialNo) return 'Serial No required.';
        if(!cylinder.finalResult) return 'Final Result required.';
        break;
      case REPORT_TYPES.DRIFTER:
        if(!drifter.serialNo) return 'Serial No required.';
        if(!drifter.checkedBy) return 'Checked By required.';
        break;
      default: break;
    }
    return null;
  };

  /* -------------- Build Payload -------------- */
  const buildPayloadForType = () => {
    switch(reportType){
      case REPORT_TYPES.AC_TEST:
        return {
          acSections:[
            {section:'Identification',data:[
              ['Make',acTest.make],['kW/HP',acTest.kw_hp],['Frame Size',acTest.frameSize],
              ['Poles',acTest.poles],['SpeedRPM',acTest.speed],['UnitNo',acTest.unitNo]
            ]},
            {section:'Electrical',data:[
              ['RatedVoltage',acTest.ratedVoltage],
              ['Connection',acTest.connection],
              ['RotorBalanced',acTest.rotorBalanced]
            ]},
            {section:'PriorRewind',data:[
              ['IR1000V',acTest.ir1000],
              ['Growler',acTest.growler],
              ['CondBurnt',acTest.condBurnt],
              ['CondWet',acTest.condWet],
              ['CondMech',acTest.condMech],
              ['CondSingle',acTest.condSingle],
              ['CondOther',acTest.condOther],
              ['AmbientTemp',acTest.ambientTemp],
              ['CoreTemp',acTest.coreTemp],
              ['CoreWattLoss',acTest.coreWatt]
            ]},
            {section:'AfterRewind',data:[
              ['TestVoltage',acTest.loadVolt],
              ['PhaseA',acTest.phaseA],
              ['PhaseB',acTest.phaseB],
              ['PhaseC',acTest.phaseC]
            ]},
            {section:'AfterAssembly',data:[
              ['PressureKV',acTest.pressureVal],
              ['PressureDurationS',acTest.pressureDur],
              ['IRPost',acTest.irPost],
              ['RunVolt',acTest.runVolt],
              ['LockedRotorA',acTest.lockedA],
              ['LockedRotorB',acTest.lockedB],
              ['LockedRotorC',acTest.lockedC],
              ['OperatingVolt',acTest.opVolt]
            ]},
            {section:'Performance',data:[
              ['BearingDE',acTest.bearingDE],
              ['BearingNDE',acTest.bearingNDE],
              ['EfficiencyPct',acTest.efficiency],
              ['VibrationDE',acTest.vibDE],
              ['VibrationNDE',acTest.vibNDE]
            ]},
            {section:'SignOff',data:[
              ['TestedBy',acTest.testedBy]
            ]}
          ]
        };
      case REPORT_TYPES.FINAL_INSPECTION:
        return {
          finalInspection:{
            description:finalInspection.description,
            finalInspectionItems: finalInspection.items.map(i => ({
              label: i.label,
              passed: !!i.passed
            })),
            signOff:[{field:'CheckedBy',value:finalInspection.testedBy}]
          }
        };
      case REPORT_TYPES.CYLINDER:
        return {
          cylinder:{
            identity:[
              ['SerialNo',cylinder.serialNo],
              ['Description',cylinder.description],
              ['RodDia',cylinder.rodDia],
              ['BarrelDia',cylinder.barrelDia],
              ['OpenCentres',cylinder.openCentres],
              ['ClosedCentres',cylinder.closedCentres]
            ],
            cylinderChecksLow:cylinderLowList.map(([k,label])=>({label,ok:!!cylinder[k]})),
            cylinderChecksHigh:cylinderHighList.map(([k,label])=>({label,ok:!!cylinder[k]})),
            results:[
              ['OilCondition',cylinder.oilCondition],
              ['OilTemp',cylinder.oilTemp],
              ['OilCleanliness',cylinder.oilCleanliness],
              ['MaxPressure',cylinder.maxPressure],
              ['FinalResult',cylinder.finalResult],
              ['TestedBy',cylinder.testedBy]
            ]
          }
        };
      case REPORT_TYPES.DRIFTER:
        return {
          drifter:{
            idSection:[
              ['DrifterType',drifter.drifterType],
              ['SerialNo',drifter.serialNo],
              ['TestingPressure',drifter.testingPressure]
            ],
            temperatures:[
              ['OilBefore',drifter.oilTempBefore],
              ['OilAfter',drifter.oilTempAfter]
            ],
            drifterPercussion:[
              {label:'Percussion Tested',ok:drifter.percussionTested},
              {label:'Percussion Time (min)',value:drifter.percussionTime},
              {label:'Percussion Remarks',value:drifter.percussionRemarks}
            ],
            drifterRotation:[
              {label:'Rotation Tested',ok:drifter.rotationTested},
              {label:'Rotation Time (min)',value:drifter.rotationTime},
              {label:'Rotation Remarks',value:drifter.rotationRemarks}
            ],
            drifterShiftNotes: drifter.notesShift ? drifterShiftNotesList : [],
            signOff:[['CheckedBy',drifter.checkedBy]]
          }
        };
      default:
        return {};
    }
  };

  /* -------------- Save -------------- */
  const save = async e => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    const user = auth.currentUser;
    if (!user) return toast.error('Please log in.');

    try {
      setSaving(true);
      const ref = doc(db,'TestReports', id);
      const base = {
        reportId,
        reportType,
        header:{...header},
        updatedAt: new Date().toISOString()
      };
      const typePayload = buildPayloadForType();

      // Clean unused sections to keep doc tidy
      const cleanup = {
        acSections: null,
        finalInspection: null,
        cylinder: null,
        drifter: null
      };
      Object.keys(typePayload).forEach(k=>delete cleanup[k]);

      await updateDoc(ref, {
        ...base,
        ...typePayload,
        ...cleanup
      });

      toast.success('Report updated ✔');
      navigate('/Jobs');
    } catch (ex) {
      console.error(ex);
      toast.error('Update failed.');
    } finally {
      setSaving(false);
    }
  };

  /* -------------- Subforms -------------- */
  const AcForm = (
    <>
      <section className="card">
        <h3>Machine Identification</h3>
        <div className="grid-3">
          <Field id="make" label="Make" value={acTest.make} onChange={changeAC}/>
          <Field id="kw_hp" label="kW / HP" value={acTest.kw_hp} onChange={changeAC}/>
          <Field id="frameSize" label="Frame Size" value={acTest.frameSize} onChange={changeAC}/>
          <Field id="poles" label="Poles" value={acTest.poles} onChange={changeAC}/>
          <Field id="speed" label="Speed (RPM)" value={acTest.speed} onChange={changeAC}/>
          <Field id="unitNo" label="Unit No" value={acTest.unitNo} onChange={changeAC}/>
        </div>
      </section>
      <section className="card">
        <h3>Electrical Configuration</h3>
        <div className="grid-3">
          <Field id="ratedVoltage" label="Rated Voltage" value={acTest.ratedVoltage} onChange={changeAC}/>
          <Field id="connection" label="Connection" value={acTest.connection} placeholder="Delta / Star" onChange={changeAC}/>
          <div className="field single-checkbox">
            <CheckItem id="rotorBalanced" label="Rotor Dynamically Balanced" checked={acTest.rotorBalanced} onChange={changeAC}/>
          </div>
        </div>
      </section>
      <section className="card">
        <h3>1. Prior to Rewind</h3>
        <div className="grid-2">
          <Field id="ir1000" label="IR @ 1000 V" value={acTest.ir1000} onChange={changeAC}/>
          <Field id="growler" label="Growler Test" value={acTest.growler} onChange={changeAC}/>
        </div>
        <div className="fieldset">
          <div className="legend">Condition Findings</div>
          <CheckGrid>
            <CheckItem id="condBurnt" label="Burnt-Out" checked={acTest.condBurnt} onChange={changeAC}/>
            <CheckItem id="condWet" label="Grease Wet" checked={acTest.condWet} onChange={changeAC}/>
            <CheckItem id="condMech" label="Mechanical Damage" checked={acTest.condMech} onChange={changeAC}/>
            <CheckItem id="condSingle" label="Single Phase" checked={acTest.condSingle} onChange={changeAC}/>
            <div className="field other-input">
              <input id="condOther" placeholder="Other issue…" value={acTest.condOther} onChange={changeAC}/>
            </div>
          </CheckGrid>
        </div>
        <h4>Core Test (after 15 min)</h4>
        <div className="grid-3">
          <Field id="ambientTemp" label="Ambient (°C)" value={acTest.ambientTemp} onChange={changeAC}/>
          <Field id="coreTemp" label="Core (°C)" value={acTest.coreTemp} onChange={changeAC}/>
          <Field id="coreWatt" label="Core Loss (W)" value={acTest.coreWatt} onChange={changeAC}/>
        </div>
      </section>
      <section className="card">
        <h3>2. After Rewind</h3>
        <div className="grid-4">
          <Field id="loadVolt" label="Test Voltage (V)" value={acTest.loadVolt} onChange={changeAC}/>
          <Field id="phaseA" label="Phase A (A)" value={acTest.phaseA} onChange={changeAC}/>
          <Field id="phaseB" label="Phase B (A)" value={acTest.phaseB} onChange={changeAC}/>
          <Field id="phaseC" label="Phase C (A)" value={acTest.phaseC} onChange={changeAC}/>
        </div>
      </section>
      <section className="card">
        <h3>3. After Assembly</h3>
        <div className="grid-3">
          <Field id="pressureVal" label="Pressure Test (kV)" value={acTest.pressureVal} onChange={changeAC}/>
          <Field id="pressureDur" label="Duration (s)" value={acTest.pressureDur} onChange={changeAC}/>
          <Field id="irPost" label="IR Post (MΩ)" value={acTest.irPost} onChange={changeAC}/>
        </div>
        <div className="grid-4">
          <Field id="runVolt" label="30 min Run V" value={acTest.runVolt} onChange={changeAC}/>
          <Field id="lockedA" label="Locked Rotor A" value={acTest.lockedA} onChange={changeAC}/>
          <Field id="lockedB" label="Locked Rotor B" value={acTest.lockedB} onChange={changeAC}/>
          <Field id="lockedC" label="Locked Rotor C" value={acTest.lockedC} onChange={changeAC}/>
        </div>
        <div className="grid-3">
          <Field id="opVolt" label="Operating Voltage (V)" value={acTest.opVolt} onChange={changeAC}/>
        </div>
      </section>
      <section className="card">
        <h3>Performance</h3>
        <div className="grid-3">
          <Field id="bearingDE" label="Bearing DE" value={acTest.bearingDE} onChange={changeAC}/>
          <Field id="bearingNDE" label="Bearing NDE" value={acTest.bearingNDE} onChange={changeAC}/>
          <Field id="efficiency" label="Efficiency (%)" value={acTest.efficiency} onChange={changeAC}/>
          <Field id="vibDE" label="Vibration DE" value={acTest.vibDE} onChange={changeAC}/>
          <Field id="vibNDE" label="Vibration NDE" value={acTest.vibNDE} onChange={changeAC}/>
        </div>
      </section>
      <section className="card">
        <h3>Sign-off</h3>
        <Field id="testedBy" label="Tested By" value={acTest.testedBy} onChange={changeAC}/>
      </section>
    </>
  );

  const FinalInspectionForm = (
    <>
      <section className="card">
        <h3>Inspection Checklist</h3>
        <Field
          id="description"
          label="Description"
          value={finalInspection.description}
          onChange={changeFIDescOrTester}
        />

        {/* Add new item row */}
        <div className="fi-add-row">
          <input
            className="fi-add-input"
            placeholder="Add checklist item…"
            value={fiNewLabel}
            onChange={e => setFiNewLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFiItem(); } }}
          />
          <button type="button" className="fi-add-btn" onClick={addFiItem}>
            + Add
          </button>
        </div>

        {/* List */}
        <div className="fi-list">
          {finalInspection.items.map(it => (
            <div className="fi-item-row" key={it.id}>
              <input
                type="checkbox"
                checked={it.passed}
                onChange={e => toggleFiPassed(it.id, e.target.checked)}
                title="Passed"
              />
              <input
                className="fi-item-label"
                value={it.label}
                onChange={e => editFiLabel(it.id, e.target.value)}
                placeholder="Item label…"
              />
              <button
                type="button"
                className="fi-del-btn"
                onClick={() => deleteFiItem(it.id)}
                aria-label="Delete item"
              >
                ×
              </button>
            </div>
          ))}
          {!finalInspection.items.length && (
            <p className="muted small" style={{marginTop:'.4rem'}}>No checklist items yet.</p>
          )}
        </div>
      </section>

      <section className="card">
        <h3>Sign-off</h3>
        <Field
          id="testedBy"
          label="Checked By"
          value={finalInspection.testedBy}
          onChange={changeFIDescOrTester}
        />
      </section>
    </>
  );

  const CylinderForm = (
    <>
      <section className="card">
        <h3>Cylinder Identification</h3>
        <div className="grid-3">
          <Field id="serialNo" label="Serial No" value={cylinder.serialNo} onChange={changeCylinder}/>
          <Field id="description" label="Description" value={cylinder.description} onChange={changeCylinder}/>
          <Field id="rodDia" label="Rod Ø (mm)" value={cylinder.rodDia} onChange={changeCylinder}/>
          <Field id="barrelDia" label="Barrel Ø (mm)" value={cylinder.barrelDia} onChange={changeCylinder}/>
          <Field id="openCentres" label="Open Centres" value={cylinder.openCentres} onChange={changeCylinder}/>
          <Field id="closedCentres" label="Closed Centres" value={cylinder.closedCentres} onChange={changeCylinder}/>
        </div>
      </section>
      <section className="card">
        <h3>Low Pressure Test (&lt;10 bar)</h3>
        <CheckGrid>
          {cylinderLowList.map(([k,label])=>(
            <CheckItem key={k} id={k} label={label} checked={cylinder[k]} onChange={changeCylinder}/>
          ))}
        </CheckGrid>
      </section>
      <section className="card">
        <h3>High Pressure Test</h3>
        <CheckGrid>
          {cylinderHighList.map(([k,label])=>(
            <CheckItem key={k} id={k} label={label} checked={cylinder[k]} onChange={changeCylinder}/>
          ))}
        </CheckGrid>
        <div className="grid-4 mt1">
          <Field id="oilCondition" label="Oil Condition" value={cylinder.oilCondition} onChange={changeCylinder}/>
          <Field id="oilTemp" label="Oil Temp (°C)" value={cylinder.oilTemp} onChange={changeCylinder}/>
          <Field id="oilCleanliness" label="Oil Cleanliness" value={cylinder.oilCleanliness} onChange={changeCylinder}/>
          <Field id="maxPressure" label="Max Pressure (bar)" value={cylinder.maxPressure} onChange={changeCylinder}/>
        </div>
      </section>
      <section className="card">
        <h3>Result</h3>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="finalResult">Final Result</label>
            <select id="finalResult" value={cylinder.finalResult} onChange={changeCylinder}>
              <option value="">Select</option>
              <option value="PASSED">PASSED</option>
              <option value="FAILED">FAILED</option>
            </select>
          </div>
          <Field id="testedBy" label="Name & Signature" value={cylinder.testedBy} onChange={changeCylinder}/>
        </div>
      </section>
    </>
  );

  const DrifterForm = (
    <>
      <section className="card">
        <h3>Drifter Identification</h3>
        <div className="grid-3">
          <Field id="drifterType" label="Drifter Type" value={drifter.drifterType} onChange={changeDrifter}/>
          <Field id="serialNo" label="Serial No" value={drifter.serialNo} onChange={changeDrifter}/>
          <Field id="testingPressure" label="Testing Pressure (bar)" value={drifter.testingPressure} onChange={changeDrifter}/>
        </div>
      </section>
      <section className="card">
        <h3>Oil Temperature</h3>
        <div className="grid-2">
          <Field id="oilTempBefore" label="Before (°C)" value={drifter.oilTempBefore} onChange={changeDrifter}/>
          <Field id="oilTempAfter" label="After (°C)" value={drifter.oilTempAfter} onChange={changeDrifter}/>
        </div>
      </section>
      <section className="card">
        <h3>Percussion Mechanism</h3>
        <CheckGrid>
          <CheckItem id="percussionTested" label="Tested" checked={drifter.percussionTested} onChange={changeDrifter}/>
        </CheckGrid>
        <div className="grid-2">
          <Field id="percussionTime" label="Test Time (min)" value={drifter.percussionTime} onChange={changeDrifter}/>
          <Field id="percussionRemarks" label="Remarks" value={drifter.percussionRemarks} onChange={changeDrifter}/>
        </div>
      </section>
      <section className="card">
        <h3>Rotation Mechanism</h3>
        <CheckGrid>
          <CheckItem id="rotationTested" label="Tested" checked={drifter.rotationTested} onChange={changeDrifter}/>
        </CheckGrid>
        <div className="grid-2">
          <Field id="rotationTime" label="Test Time (min)" value={drifter.rotationTime} onChange={changeDrifter}/>
          <Field id="rotationRemarks" label="Remarks" value={drifter.rotationRemarks} onChange={changeDrifter}/>
        </div>
      </section>
      <section className="card">
        <h3>Shift Notes</h3>
        <CheckGrid>
          <CheckItem id="notesShift" label="Include Standard Shift Notes" checked={drifter.notesShift} onChange={changeDrifter}/>
        </CheckGrid>
        {drifter.notesShift && (
          <div className="shift-notes">
            <ul>{drifterShiftNotesList.map(n=> <li key={n}>{n}</li>)}</ul>
          </div>
        )}
      </section>
      <section className="card">
        <h3>Sign-off</h3>
        <Field id="checkedBy" label="Checked By" value={drifter.checkedBy} onChange={changeDrifter}/>
      </section>
    </>
  );

  const renderBody = useMemo(() => {
    switch(reportType){
      case REPORT_TYPES.AC_TEST: return AcForm;
      case REPORT_TYPES.FINAL_INSPECTION: return FinalInspectionForm;
      case REPORT_TYPES.CYLINDER: return CylinderForm;
      case REPORT_TYPES.DRIFTER: return DrifterForm;
      default: return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, acTest, finalInspection, cylinder, drifter, fiNewLabel]); // state deps so JSX refreshes

  if (loading) {
    return (
      <div className="edit-loading">
        <FaSpinner className="spin" size={46}/>
        <p>Loading report...</p>
      </div>
    );
  }

  return (
    <div className="edit-page">
      <ToastContainer position="top-right" autoClose={3000}/>
      <Sidebar/>
      <main className="edit-main">
        <div className="title-row">
          <h2>Edit {REPORT_TYPE_LABELS[reportType]}</h2>
          <span className="type-badge">{reportType}</span>
        </div>

        <div className="tabs">
          {Object.values(REPORT_TYPES).map(rt=>(
            <button
              key={rt}
              type="button"
              className={`tab-btn ${rt===reportType?'active':''}`}
              onClick={()=>setReportType(rt)}
              disabled={saving}
            >{REPORT_TYPE_LABELS[rt]}</button>
          ))}
        </div>

        <form onSubmit={save} autoComplete="off">
          <section className="card header-card">
            <h3>Report Header</h3>
            <div className="grid-5">
              <div className="field">
                <label>Report ID</label>
                <input value={reportId} readOnly />
              </div>
              <div className="field">
                <label htmlFor="customer">Customer</label>
                <select id="customer" value={header.customer} onChange={changeHeader}>
                  <option value="">Select Customer</option>
                  {customers.map(c=>(
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="jobId">Job No.</label>
                <select id="jobId" value={header.jobId} onChange={changeHeader}>
                  <option value="">Select Job</option>
                  {jobs.map(j=><option key={j} value={j}>{j}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="status">Status</label>
                <select id="status" value={header.status} onChange={changeHeader}>
                  <option>Pending</option>
                  <option>In Progress</option>
                  <option>Completed</option>
                  <option>On Hold</option>
                  <option>Cancelled</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="date">Date</label>
                <input id="date" type="date" value={header.date} onChange={changeHeader}/>
              </div>
            </div>
          </section>

          {renderBody}

          <div className="submit-wrap">
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </main>

      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={52}/>
          <p>Updating…</p>
        </div>
      )}
    </div>
  );
}
